import { useState, useEffect, useCallback, useRef } from 'react';
import { usePublicWebSocketContext } from '../contexts/PublicWebSocketContext';

const API_URL = process.env.REACT_APP_API_URL || `http://${window.location.hostname}:5000`;

/**
 * Hook for managing overlay layer visibility
 * Listens to WebSocket events for layer toggles
 * Also fetches current state via HTTP on mount and reconnect as failsafe
 */
export const useOverlayLayers = (matchId) => {
  const { socket, connected } = usePublicWebSocketContext();
  const [layers, setLayers] = useState({});
  const lastFetchRef = useRef(0);

  // Fetch current layer state via HTTP (failsafe)
  const fetchLayerState = useCallback(async () => {
    if (!matchId) return;
    try {
      const response = await fetch(`${API_URL}/api/overlay/${matchId}/state`);
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.data) {
          console.log('[useOverlayLayers] HTTP fetch layer state:', data.data);
          setLayers(data.data);
        }
      }
    } catch (error) {
      console.error('[useOverlayLayers] Error fetching layer state:', error);
    }
  }, [matchId]);

  // Fetch on mount
  useEffect(() => {
    fetchLayerState();
  }, [fetchLayerState]);

  // Fetch on reconnect
  useEffect(() => {
    if (!socket || !connected) return;

    const handleReconnect = () => {
      console.log('[useOverlayLayers] Socket reconnected, fetching layer state');
      fetchLayerState();
    };

    socket.on('connect', handleReconnect);
    return () => {
      socket.off('connect', handleReconnect);
    };
  }, [socket, connected, fetchLayerState]);

  // Periodic HTTP poll every 10s as failsafe
  useEffect(() => {
    if (!matchId) return;
    const interval = setInterval(() => {
      fetchLayerState();
    }, 3000);
    return () => clearInterval(interval);
  }, [matchId, fetchLayerState]);

  // Stable handler without stale closures
  const handleLayerToggle = useCallback((data) => {
    console.log('[useOverlayLayers] WS layer toggle:', data.layerId, data.visible);
    setLayers(prev => ({
      ...prev,
      [data.layerId]: data.visible
    }));
  }, []);

  // Stable handler for layers update
  const handleLayersUpdate = useCallback((data) => {
    setLayers(data.layers);
  }, []);

  // Re-register listener when socket reconnects
  useEffect(() => {
    if (!socket || !connected) {
      return;
    }

    // Remove old listener before adding new one
    socket.off('overlay:layer:toggle', handleLayerToggle);
    socket.on('overlay:layer:toggle', handleLayerToggle);

    return () => {
      socket.off('overlay:layer:toggle', handleLayerToggle);
    };
  }, [socket, connected, handleLayerToggle]); // Re-run when connected changes

  // Listen for all layers update
  useEffect(() => {
    if (!socket || !connected) return;

    socket.off('overlay:layers:update', handleLayersUpdate);
    socket.on('overlay:layers:update', handleLayersUpdate);

    return () => {
      socket.off('overlay:layers:update', handleLayersUpdate);
    };
  }, [socket, connected, handleLayersUpdate]);

  return {
    connected,
    layers
  };
};
